/**
 * Searchable country-code dropdown (mouse + text search) used by phone-type
 * contact form fields. Built on the existing shadcn Popover + Command (cmdk).
 */
import React, { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command';
import { COUNTRY_CODES, getCountryByIso2, isoToFlagEmoji } from '../../constants/countryCodes';

const CountryCodeSelect = ({ value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => getCountryByIso2(value), [value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          aria-label="Select country code"
          className="flex items-center gap-1.5 px-3 py-3 border-2 border-slate-200 rounded-xl bg-white hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 transition-colors text-sm font-medium text-slate-700 whitespace-nowrap"
        >
          <span className="text-base leading-none">{isoToFlagEmoji(selected.iso2)}</span>
          <span>{selected.dialCode}</span>
          <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search country or code..." data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {COUNTRY_CODES.map((c) => (
                <CommandItem
                  key={c.iso2}
                  value={`${c.name} ${c.dialCode}`}
                  onSelect={() => {
                    onChange(c.iso2);
                    setOpen(false);
                  }}
                  data-testid={`${testId}-option-${c.iso2}`}
                  className="cursor-pointer"
                >
                  <span className="text-base leading-none mr-2">{isoToFlagEmoji(c.iso2)}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-slate-400 font-mono text-xs ml-2">{c.dialCode}</span>
                  {c.iso2 === selected.iso2 && <Check className="w-4 h-4 ml-2 text-slate-900 flex-shrink-0" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CountryCodeSelect;
